import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Upload, X, Star, Image, Plus, RefreshCw, Check } from 'lucide-react';
import { toast } from 'sonner';

import API from '../../config/api';

const ProductImageUploader = ({ 
  featuredImage = '', 
  galleryImages = [], 
  onFeaturedChange, 
  onGalleryChange 
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  // Helper to ensure full URL resolution
  const resolveUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const base = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
    return `${base}${url}`;
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    const headers = { 'Content-Type': 'multipart/form-data' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  };

  // Upload Featured Image
  const handleFeaturedUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image size must be under 10MB');
      return;
    }

    const formData = new FormData();
    formData.append('image', file);

    setUploading(true);
    try {
      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: getAuthHeaders(),
        withCredentials: true
      });

      const url = response.data.url;
      onFeaturedChange(url);

      // Also ensure featured is in gallery
      if (!galleryImages.includes(url)) {
        onGalleryChange([url, ...galleryImages.slice(0, 9)]);
      }

      toast.success('Featured image uploaded successfully');
    } catch (error) {
      console.error('Featured image upload failed:', error);
      const msg = error.response?.data?.detail || error.message || 'Failed to upload image';
      toast.error(msg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Upload Multiple Gallery Images (up to 10 total)
  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const remainingSlots = 10 - galleryImages.length;
    if (remainingSlots <= 0) {
      toast.error('Maximum 10 gallery images allowed');
      return;
    }

    const filesToUpload = files.slice(0, remainingSlots);

    const formData = new FormData();
    filesToUpload.forEach((file) => {
      formData.append('images', file);
    });

    setUploading(true);
    try {
      const response = await axios.post(`${API}/upload/images`, formData, {
        headers: getAuthHeaders(),
        withCredentials: true
      });

      const uploadedUrls = response.data.urls || [];
      const updatedGallery = [...galleryImages, ...uploadedUrls].slice(0, 10);
      onGalleryChange(updatedGallery);

      // If no featured image set yet, set the first uploaded as featured
      if (!featuredImage && updatedGallery.length > 0) {
        onFeaturedChange(updatedGallery[0]);
      }

      toast.success(`${uploadedUrls.length} image(s) uploaded successfully`);
    } catch (error) {
      console.error('Gallery upload failed:', error);
      const msg = error.response?.data?.detail || error.message || 'Failed to upload gallery images';
      toast.error(msg);
    } finally {
      setUploading(false);
      if (galleryInputRef.current) galleryInputRef.current.value = '';
    }
  };

  // Set image as Featured
  const handleSetFeatured = (url) => {
    onFeaturedChange(url);
    toast.success('Set as featured image');
  };

  // Remove image from gallery
  const handleRemoveImage = (indexToRemove) => {
    const removedUrl = galleryImages[indexToRemove];
    const updated = galleryImages.filter((_, idx) => idx !== indexToRemove);
    onGalleryChange(updated);

    // If removed image was featured, set next available or clear
    if (featuredImage === removedUrl) {
      onFeaturedChange(updated[0] || '');
    }
  };

  return (
    <div className="space-y-4">
      
      {/* ─── Featured & Main Upload Area ─── */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
        
        {/* Featured Image Preview */}
        <div className="md:col-span-5">
          <label className="block text-xs font-bold text-gray-800 mb-1.5 flex items-center justify-between">
            <span>Featured Product Image *</span>
            <span className="text-[11px] text-gray-500 font-normal">Shows on store listing</span>
          </label>

          <div className="relative aspect-square w-full bg-[#FBF4E7] rounded-2xl overflow-hidden border-2 border-dashed border-[#E7D9BC] hover:border-[#D9711F] transition-colors flex flex-col items-center justify-center group">
            {featuredImage ? (
              <>
                <img
                  src={resolveUrl(featuredImage)}
                  alt="Featured Preview"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="bg-white text-gray-900 text-xs font-semibold px-3 py-1.5 rounded-lg shadow-md hover:bg-[#F3E7D0]"
                  >
                    Change Image
                  </button>
                </div>
                <div className="absolute top-2 left-2 bg-[#6E1B34] text-white text-[10px] font-bold px-2 py-0.5 rounded-md shadow-xs flex items-center gap-1">
                  <Star className="w-3 h-3 fill-current text-[#F2A65A]" />
                  <span>Featured</span>
                </div>
              </>
            ) : (
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="cursor-pointer text-center p-6 w-full h-full flex flex-col items-center justify-center hover:bg-[#F3E7D0]/40 transition-colors"
              >
                <Upload className="w-8 h-8 text-[#D9711F] mb-2" />
                <p className="text-xs font-bold text-[#4E1225]">Click to Upload Featured Image</p>
                <p className="text-[10px] text-gray-500 mt-1">PNG, JPG, WebP up to 10MB</p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFeaturedUpload}
            />
          </div>
        </div>

        {/* Gallery Multi-Upload Box */}
        <div className="md:col-span-7 space-y-2">
          <div className="flex items-center justify-between">
            <label className="block text-xs font-bold text-gray-800">
              Product Gallery Images (Max 10)
            </label>
            <span className="text-[11px] font-semibold text-[#83725B]">
              {galleryImages.length} / 10 images
            </span>
          </div>

          {/* Upload Trigger Area */}
          <div
            onClick={() => galleryImages.length < 10 && galleryInputRef.current?.click()}
            className={`border-2 border-dashed rounded-2xl p-4 text-center cursor-pointer transition-all ${
              galleryImages.length >= 10
                ? 'bg-gray-100 border-gray-200 cursor-not-allowed opacity-60'
                : 'bg-[#FBF4E7]/40 border-[#E7D9BC] hover:border-[#D9711F] hover:bg-[#FBF4E7]'
            }`}
          >
            <input
              ref={galleryInputRef}
              type="file"
              multiple
              accept="image/*"
              className="hidden"
              disabled={galleryImages.length >= 10 || uploading}
              onChange={handleGalleryUpload}
            />

            <div className="flex items-center justify-center gap-2 text-xs font-bold text-[#4E1225]">
              {uploading ? (
                <>
                  <RefreshCw className="w-4 h-4 text-[#D9711F] animate-spin" />
                  <span>Uploading image(s)...</span>
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 text-[#D9711F]" />
                  <span>Upload Gallery Images Directly from Computer</span>
                </>
              )}
            </div>
            <p className="text-[11px] text-gray-500 mt-0.5">Select multiple images at once (up to 10 total)</p>
          </div>

          {/* Gallery Thumbnails List */}
          {galleryImages.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2.5 pt-2">
              {galleryImages.map((imgUrl, index) => {
                const isFeatured = featuredImage === imgUrl;
                return (
                  <div
                    key={index}
                    className={`group relative aspect-square rounded-xl overflow-hidden border-2 bg-white transition-all shadow-xs ${
                      isFeatured ? 'border-[#D9711F] ring-2 ring-[#D9711F]/30' : 'border-[#E7D9BC]'
                    }`}
                  >
                    <img
                      src={resolveUrl(imgUrl)}
                      alt={`Gallery ${index + 1}`}
                      className="w-full h-full object-cover"
                    />

                    {/* Delete button */}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleRemoveImage(index); }}
                      className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-xs hover:bg-red-600"
                      title="Remove image"
                    >
                      <X className="w-3 h-3" />
                    </button>

                    {/* Set Featured overlay */}
                    {!isFeatured && (
                      <button
                        type="button"
                        onClick={() => handleSetFeatured(imgUrl)}
                        className="absolute bottom-1 left-1 right-1 bg-black/70 hover:bg-[#6E1B34] text-white text-[9px] font-bold py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity text-center"
                      >
                        Set Main
                      </button>
                    )}

                    {isFeatured && (
                      <div className="absolute top-1 left-1 bg-[#6E1B34] text-white p-0.5 rounded">
                        <Star className="w-2.5 h-2.5 fill-current text-[#F2A65A]" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>

    </div>
  );
};

export default ProductImageUploader;
